<!-- Loading -->
<div *ngIf="isLoading" class="max-w-[1440px] mx-auto px-4 md:px-6 py-6">
  <div class="grid grid-cols-1 md:grid-cols-[1fr_380px] gap-6">
    <div class="aspect-[4/3] bg-gray-200 animate-pulse"></div>
    <div class="space-y-3"><div class="h-6 bg-gray-200 rounded w-3/4 animate-pulse"></div><div class="grid grid-cols-4 gap-1.5"><div *ngFor="let i of [1,2,3,4,5,6,7,8]" class="aspect-square bg-gray-100 animate-pulse"></div></div><div class="h-20 bg-gray-100 rounded animate-pulse"></div></div>
  </div>
</div>

<!-- Error -->
<div *ngIf="!isLoading && loadError" class="min-h-[60vh] flex items-center justify-center">
  <div class="text-center"><p class="text-gray-400 text-lg" style="font-family:'Roboto Slab',serif;">Could not load product.</p><button (click)="navigateToListing()" class="mt-4 bg-[#600b0b] text-white px-6 py-3 text-xs font-bold uppercase tracking-wider">Browse Collage Sets</button></div>
</div>

<!-- PDP -->
<div *ngIf="!isLoading && collageLayout" class="pb-16 md:pb-0" style="font-family:sans-serif;">
  <div class="max-w-[1440px] mx-auto px-3 md:px-6 py-3 md:py-6">
    <div class="grid grid-cols-1 md:grid-cols-[3fr_2fr] gap-4 md:gap-5">

      <!-- ══ LEFT ══ -->
      <div class="min-w-0">
        <!-- Fixed container for both views -->
        <div class="relative w-full h-[280px] md:h-[500px] overflow-hidden bg-[#f5f0eb]">
          <!-- Space View (room mockup) — scroll to zoom, drag to pan -->
          <div *ngIf="showSpaceView && spaceImages[activeImageIndex]"
            class="absolute inset-0 select-none"
            [style.cursor]="zoomScale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in'"
            (wheel)="onZoomWheel($event)"
            (mousedown)="onZoomMouseDown($event)"
            (mousemove)="onZoomMouseMove($event)"
            (mouseup)="onZoomMouseUp()"
            (mouseleave)="onZoomMouseUp()"
            (touchstart)="onZoomTouchStart($event)"
            (touchmove)="onZoomTouchMove($event)"
            (touchend)="onZoomMouseUp()">
            <img [src]="spaceImages[activeImageIndex]" [alt]="collageLayout.caption"
              class="w-full h-full object-contain pointer-events-none"
              [style.transform]="'scale(' + zoomScale + ') translate(' + zoomTranslateX + 'px, ' + zoomTranslateY + 'px)'"
              [style.transition]="isDragging ? 'none' : 'transform 0.2s ease-out'" />
          </div>

          <!-- Frame Preview Canvas (editable) -->
          <div *ngIf="!showSpaceView" class="absolute inset-0 bg-white overflow-auto flex items-center justify-center">
            <div class="relative" [style.width.px]="canvasWidth" [style.height.px]="canvasHeight">
              <div *ngFor="let frame of frameDetail; let i = index"
                [ngStyle]="getFramePreviewStyle(frame)" (click)="onFrameClick(i)"
                class="hover:opacity-80 transition-opacity" [title]="'Click to upload — F' + (i+1)">
                <div [ngStyle]="{'padding.px': frame.mount_size_1 || 0, 'background': frame.mount1background || 'transparent'}">
                  <div [ngStyle]="{'padding.px': frame.mount_size_2 || 0, 'background': frame.mount2background || 'transparent'}">
                    <div [ngStyle]="{'padding.px': frame.mount_size_3 || 0, 'background': frame.mount3background || 'transparent'}">
                      <div *ngIf="isFrameEmpty(frame)" [ngStyle]="getImageStyle(frame)" class="bg-gray-200 flex items-center justify-center text-center">
                        <div><strong class="text-[10px] text-gray-500 block">F{{ i + 1 }}</strong><span class="text-[9px] text-gray-400">{{ frame.width }} X {{ frame.height }}</span></div>
                      </div>
                      <img *ngIf="!isFrameEmpty(frame)" [src]="frame.backgroundImage" [ngStyle]="getImageStyle(frame)" [ngClass]="frame.image_class || ''" class="object-cover block" (error)="frame.backgroundImage = ''" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Thumbnails: small, single horizontal row -->
        <div class="flex gap-1.5 mt-2 overflow-x-auto no-scrollbar pb-1">
          <!-- Customize thumbnail (first) -->
          <div (click)="showSpaceView = false; resetZoom()"
            [class]="'cursor-pointer w-[55px] md:w-[65px] h-[40px] md:h-[48px] shrink-0 overflow-hidden border relative ' + (!showSpaceView ? 'border-[#600b0b]' : 'border-gray-200 hover:border-gray-400')">
            <img [src]="spaceImages[0] || collageLayout.list_thumbnail" alt="Customize" class="w-full h-full object-cover" />
            <div *ngIf="!showSpaceView" class="absolute top-0.5 right-0.5 w-3 h-3 bg-[#4CAF50] rounded-full flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
          </div>
          <!-- Space/room thumbnails -->
          <div *ngFor="let img of spaceImages; let i = index" (click)="activeImageIndex = i; showSpaceView = true; resetZoom()"
            [class]="'cursor-pointer w-[55px] md:w-[65px] h-[40px] md:h-[48px] shrink-0 overflow-hidden border ' + (showSpaceView && activeImageIndex === i ? 'border-[#600b0b]' : 'border-gray-200 hover:border-gray-400')">
            <img [src]="img" [alt]="'Room ' + (i+1)" class="w-full h-full object-cover" />
          </div>
        </div>

        <!-- About -->
        <div *ngIf="collageLayout.description" class="mt-4">
          <h3 class="text-sm font-bold text-gray-900 mb-1">About Collage Set</h3>
          <p *ngIf="collageLayout.collage_type?.name" class="text-xs font-bold text-gray-700 mb-2">{{ collageLayout.collage_type.name }}</p>
          <p class="text-xs text-gray-600 leading-relaxed">{{ collageLayout.description }}</p>
        </div>
      </div>

      <!-- ══ RIGHT ══ -->
      <div class="md:border-l md:border-gray-100 md:pl-5">
        <!-- Title -->
        <div class="flex items-start justify-between">
          <h1 class="text-base md:text-lg font-bold text-gray-900 leading-snug pr-2" style="font-family:'Roboto Slab',serif;">{{ collageLayout.caption }}</h1>
          <div class="flex gap-2 shrink-0">
            <button (click)="toggleWishlist($event)"
              [class]="'transition-colors ' + (isWishlisted() ? 'text-[#600b0b]' : 'text-gray-400 hover:text-[#600b0b]')">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                [attr.fill]="isWishlisted() ? 'currentColor' : 'none'"
                stroke="currentColor" stroke-width="1.5"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
            </button>
          </div>
        </div>

        <!-- UPLOAD PHOTOS -->
        <div class="mt-4">
          <p class="text-[11px] font-bold text-gray-800 uppercase tracking-wider mb-2">UPLOAD PHOTOS</p>
          <div class="grid grid-cols-4 gap-1.5">
            <div *ngFor="let frame of frameDetail; let i = index" (click)="onFrameClick(i)"
              [class]="'aspect-square border flex items-center justify-center cursor-pointer hover:border-[#600b0b] transition-colors ' + (selectedFrameIndex === i ? 'border-[#600b0b] border-2' : 'border-gray-300') + ' ' + (isFrameEmpty(frame) ? 'bg-gray-50' : '')">
              <div *ngIf="isFrameEmpty(frame)" class="text-[7px] md:text-[8px] text-gray-400 text-center leading-tight px-0.5">Upload<br>Image<br>F{{ i + 1 }}</div>
              <img *ngIf="!isFrameEmpty(frame)" [src]="frame.backgroundImage" class="w-full h-full object-cover" (error)="frame.backgroundImage = ''" />
            </div>
          </div>
        </div>

        <!-- CUSTOMIZE FRAMES -->
        <div *ngIf="availableFrameOptions.length > 0" class="mt-5">
          <p class="text-[11px] font-bold text-gray-800 uppercase tracking-wider mb-2">CUSTOMIZE FRAMES</p>
          <div class="flex gap-2.5 flex-wrap">
            <div *ngFor="let f of availableFrameOptions" (click)="selectFrameOption(f)"
              [class]="'relative cursor-pointer border-2 p-0.5 w-14 h-14 md:w-16 md:h-16 ' + (selectedFrameOption?.id === f.id ? 'border-[#600b0b]' : 'border-gray-200 hover:border-gray-400')">
              <img [src]="f.L_frame_image || f.whole_frame_image || ''" [alt]="f.name" class="w-full h-full object-contain" (error)="$any($event.target).src='assets/images/product/product_type/black-frame.png'" />
              <div *ngIf="selectedFrameOption?.id === f.id" class="absolute -top-1.5 -right-1.5 w-4 h-4 bg-[#4CAF50] rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
            </div>
          </div>
          <div class="flex gap-2.5 flex-wrap mt-1">
            <div *ngFor="let f of availableFrameOptions" class="w-14 md:w-16 text-center"><span class="text-[7px] text-gray-500">{{ f.name }}</span></div>
          </div>
        </div>

        <!-- Print Surface / Glass / Size -->
        <div class="mt-4 space-y-2 border-t border-gray-100 pt-3">
          <div class="flex items-center justify-between">
            <span class="text-[11px] font-bold text-gray-800">Print Surface</span>
            <span class="text-[11px] text-gray-600 border border-gray-300 rounded px-3 py-1 min-w-[120px] text-center">{{ printSurface?.display_name || 'Matte 175 GSM' }}</span>
          </div>
          <div class="flex items-center justify-between">
            <span class="text-[11px] font-bold text-gray-800">Glass</span>
            <span class="text-[11px] text-gray-600 border border-gray-300 rounded px-3 py-1 min-w-[120px] text-center">{{ currentGlass?.name || 'Regular Glass' }}</span>
          </div>
          <div class="flex items-center justify-between">
            <span class="text-[11px] font-bold text-gray-800">Finished Size</span>
            <span class="text-[11px] text-gray-600 border border-gray-300 rounded px-3 py-1 min-w-[120px] text-center">{{ collageLayout.final_size || 'N/A' }} inch</span>
          </div>
        </div>

        <!-- PRICING + BUTTONS (side by side on desktop) -->
        <div class="mt-4 border-t border-gray-200 pt-3 flex gap-4">
          <!-- Price -->
          <div class="flex-1">
            <div *ngIf="originalPrice > finalPrice" class="text-sm text-gray-400 line-through">₹{{ originalPrice | number:'1.2-2' }}</div>
            <div class="text-xl font-bold text-gray-900">₹{{ finalPrice | number:'1.2-2' }}</div>
            <p *ngIf="discountRate > 0" class="text-xs font-bold text-[#600b0b]">SAVE {{ discountRate }}%</p>
            <p class="text-[9px] text-gray-400 mt-1">Excluding shipping fee</p>
            <!-- <button (click)="openPricingDetail()" class="text-[10px] text-[#600b0b] font-medium hover:underline">View pricing detail</button> -->
          </div>
          <!-- Buttons -->
          <div class="shrink-0 flex flex-col gap-2 w-[160px]">
            <button (click)="addToCart(true)" [disabled]="isAddingToCart || !finalPrice || finalPrice <= 0"
              class="bg-[#600b0b] text-white py-2.5 text-[9px] font-bold uppercase tracking-[0.2em] flex items-center justify-center gap-1.5 hover:bg-[#8c1c1c] disabled:opacity-40 disabled:cursor-not-allowed">
              <svg *ngIf="!isAddingToCart" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>
              <span *ngIf="isAddingToCart" class="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              BUY NOW
            </button>
            <button (click)="addToCart(false)" [disabled]="isAddingToCart || !finalPrice || finalPrice <= 0"
              class="border-2 border-[#600b0b] text-[#600b0b] py-2.5 text-[9px] font-bold uppercase tracking-[0.2em] flex items-center justify-center gap-1.5 hover:bg-[#600b0b] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed">
              <svg *ngIf="!isAddingToCart" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>
              <span *ngIf="isAddingToCart" class="w-3 h-3 border-2 border-[#600b0b] border-t-transparent rounded-full animate-spin"></span>
              ADD TO CART
            </button>
          </div>
        </div>

        <!-- Delivery -->
        <div class="mt-4 border-t border-gray-200 pt-3">
          <p class="text-xs font-bold text-gray-800">Traditional Delivery</p>
          <p class="text-[10px] text-gray-500">Estimated delivery in 7 working days</p>
        </div>

        <!-- Trust badges -->
        <div class="mt-4 border-t border-gray-200 pt-4 grid grid-cols-3 gap-2 text-center">
          <div><div class="w-10 h-10 mx-auto mb-1 flex items-center justify-center"><svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#666" stroke-width="1"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg></div><p class="text-[9px] text-gray-600 font-medium">We ship<br>worldwide</p></div>
          <div><div class="w-10 h-10 mx-auto mb-1 flex items-center justify-center"><svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#666" stroke-width="1"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg></div><p class="text-[9px] text-gray-600 font-medium">Robust<br>Packaging</p></div>
          <div><div class="w-10 h-10 mx-auto mb-1 flex items-center justify-center"><svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#666" stroke-width="1"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg></div><p class="text-[9px] text-gray-600 font-medium">Premium<br>Quality</p></div>
        </div>
      </div>
    </div>
  </div>

  <!-- RELATED -->
  <div *ngIf="relatedProducts.length > 0" class="bg-[#faf8f5] py-8">
    <div class="max-w-[1440px] mx-auto px-4 md:px-6">
      <div class="text-center mb-6">
        <h2 class="text-xl md:text-2xl text-gray-900" style="font-family:'Roboto Slab',serif;">More Related Collage</h2>
        <div class="flex items-center justify-center mt-2"><img src="assets/images/H1-separator.png" alt="" class="h-5" /></div>
      </div>
      <div class="relative">
        <div class="flex gap-4 overflow-x-auto no-scrollbar pb-2" #relatedScroll>
          <div *ngFor="let item of relatedProducts" (click)="navigateToProduct(item)" class="shrink-0 w-[180px] md:w-[220px] cursor-pointer group relative">
            <div class="overflow-hidden bg-gray-50"><img [src]="item.list_thumbnail" [alt]="item.caption" class="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-500" /></div>
            <button (click)="toggleWishlistRelated(item, $event)"
              class="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center shadow hover:scale-110 transition-transform"
              [ngClass]="isRelatedWishlisted(item) ? 'text-[#600b0b]' : 'text-gray-500 hover:text-[#600b0b]'">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                [attr.fill]="isRelatedWishlisted(item) ? 'currentColor' : 'none'"
                stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
            </button>
            <h4 class="text-xs md:text-sm font-bold text-gray-900 mt-2 truncate group-hover:text-[#600b0b]" style="font-family:'Roboto',sans-serif;">{{ item.caption }}</h4>
            <p class="text-[10px] text-[#600b0b] font-medium mt-0.5">Starts at ₹{{ getRelatedPrice(item) | number:'1.2-2' }}</p>
          </div>
        </div>
        <button (click)="scrollRelated(-1)" class="absolute left-0 top-[35%] -translate-y-1/2 w-8 h-8 bg-white shadow-lg rounded-full flex items-center justify-center z-10"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg></button>
        <button (click)="scrollRelated(1)" class="absolute right-0 top-[35%] -translate-y-1/2 w-8 h-8 bg-white shadow-lg rounded-full flex items-center justify-center z-10"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg></button>
      </div>
    </div>
  </div>

  <!-- Mobile sticky -->
  <div class="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40 md:hidden" style="padding-bottom: env(safe-area-inset-bottom, 0px);">
    <div class="flex items-center px-3 py-2">
      <div class="flex-1"><div class="text-base font-bold">₹{{ finalPrice | number:'1.2-2' }}</div><span *ngIf="discountRate > 0" class="text-[9px] text-[#600b0b] font-bold">SAVE {{ discountRate }}%</span></div>
      <div class="flex gap-2">
        <button (click)="addToCart(true)" [disabled]="isAddingToCart || !finalPrice || finalPrice <= 0"
          class="bg-[#600b0b] text-white px-4 py-2.5 text-[9px] font-bold uppercase tracking-wider disabled:opacity-40">
          {{ isAddingToCart ? 'Adding...' : 'Buy Now' }}
        </button>
        <button (click)="addToCart(false)" [disabled]="isAddingToCart || !finalPrice || finalPrice <= 0"
          class="border border-[#600b0b] text-[#600b0b] px-3 py-2.5 text-[9px] font-bold uppercase tracking-wider disabled:opacity-40">
          {{ isAddingToCart ? '...' : 'Cart' }}
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Upload dialog rendered at root level via app.component.html -->
